import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    FileText, 
    Upload, 
    Sparkles, 
    Download, 
    Eye, 
    Trash2,
    Loader2,
    Clock,
    CheckCircle,
    Plus,
    BookOpen,
    List,
    HelpCircle,
    FileQuestion,
    Zap,
    ChevronRight,
    X
} from 'lucide-react';

const DOC_TYPES = [
    { value: 'comprehensive', label: 'Comprehensive Guide', icon: BookOpen, desc: 'Full reference document' },
    { value: 'quick_reference', label: 'Quick Reference', icon: Zap, desc: 'Condensed key points' },
    { value: 'glossary', label: 'Glossary', icon: List, desc: 'Key terminology' },
    { value: 'faq', label: 'FAQ', icon: HelpCircle, desc: 'Common questions' },
    { value: 'cheatsheet', label: 'Cheat Sheet', icon: FileQuestion, desc: 'Tips & shortcuts' }
];

const ReferenceDocs = () => {
    const { api } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [topics, setTopics] = useState([]);
    const [modules, setModules] = useState([]);
    
    // Dialog state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [activeTab, setActiveTab] = useState('generate');
    
    // Generate form
    const [generateForm, setGenerateForm] = useState({
        sourceType: 'module',
        module_id: '',
        topic_id: '',
        title: '',
        content_type: 'comprehensive',
        custom_instructions: ''
    });
    
    // Upload form
    const [uploadForm, setUploadForm] = useState({
        module_id: '',
        topic_id: '',
        title: '',
        description: '',
        file: null
    });
    
    // Viewer state
    const [viewerOpen, setViewerOpen] = useState(false);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [docContent, setDocContent] = useState(null);

    useEffect(() => {
        fetchDocuments();
        fetchTopicsAndModules();
    }, []);

    const fetchDocuments = async () => {
        try {
            const response = await api.get('/reference-docs');
            setDocuments(response.data);
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTopicsAndModules = async () => {
        try {
            const [topicsRes, modulesRes] = await Promise.all([
                api.get('/topics'),
                api.get('/modules')
            ]);
            setTopics(topicsRes.data);
            setModules(modulesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        }
    };

    const handleGenerate = async () => {
        const { sourceType, module_id, topic_id, title, content_type, custom_instructions } = generateForm;
        
        if (sourceType === 'module' && !module_id) {
            toast.error('Please select a module');
            return;
        }
        if (sourceType === 'topic' && !topic_id) {
            toast.error('Please select a topic');
            return;
        }

        setGenerating(true);
        try {
            const payload = { 
                title: title || `Reference Document`, 
                content_type, 
                custom_instructions 
            };
            if (sourceType === 'module') payload.module_id = module_id;
            else payload.topic_id = topic_id;

            const response = await api.post('/reference-docs/generate', payload);
            toast.info('Generation started', { description: response.data.message });
            setShowCreateDialog(false);
            
            pollDocumentStatus(response.data.document_id);
        } catch (error) {
            toast.error('Failed to generate', {
                description: error.response?.data?.detail || 'An error occurred'
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleUpload = async () => {
        if (!uploadForm.file) {
            toast.error('Please select a file');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadForm.file);
            if (uploadForm.module_id) formData.append('module_id', uploadForm.module_id);
            if (uploadForm.topic_id) formData.append('topic_id', uploadForm.topic_id);
            if (uploadForm.title) formData.append('title', uploadForm.title);
            if (uploadForm.description) formData.append('description', uploadForm.description);

            await api.post('/reference-docs/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success('Document uploaded successfully');
            setShowCreateDialog(false);
            fetchDocuments();
        } catch (error) {
            toast.error('Upload failed', {
                description: error.response?.data?.detail || 'An error occurred'
            });
        } finally {
            setUploading(false);
        }
    };

    const pollDocumentStatus = async (docId) => {
        const poll = setInterval(async () => {
            try {
                const response = await api.get(`/reference-docs/${docId}`);
                if (response.data.status === 'completed') {
                    clearInterval(poll);
                    toast.success('Document ready!');
                    fetchDocuments();
                } else if (response.data.status === 'failed') {
                    clearInterval(poll);
                    toast.error('Generation failed', { description: response.data.error });
                    fetchDocuments();
                }
            } catch (error) {
                clearInterval(poll);
            }
        }, 3000);
    };

    const viewDocument = async (doc) => {
        try {
            const response = await api.get(`/reference-docs/${doc.id}`);
            setDocContent(response.data);
            setCurrentDoc(doc);
            setViewerOpen(true);
        } catch (error) {
            toast.error('Failed to load document');
        }
    };

    const downloadDocument = async (doc) => {
        try {
            const response = await api.get(`/reference-docs/${doc.id}/download`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.filename || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error('Download failed');
        }
    };

    const deleteDocument = async (id) => {
        try {
            await api.delete(`/reference-docs/${id}`);
            toast.success('Document deleted');
            fetchDocuments();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const renderDocContent = (content) => {
        if (!content || !content.content) return null;
        const data = content.content;
        
        return (
            <div className="space-y-6">
                {/* Sections */}
                {data.sections?.map((section, idx) => (
                    <div key={idx} className="space-y-3">
                        <h2 className="text-xl font-semibold text-primary">{section.heading}</h2>
                        <p className="text-foreground whitespace-pre-wrap">{section.content}</p>
                        {section.subsections?.map((sub, subIdx) => (
                            <div key={subIdx} className="ml-4 pl-4 border-l-2 border-primary/20">
                                <h3 className="text-lg font-medium">{sub.subheading}</h3>
                                <p className="text-muted-foreground">{sub.content}</p>
                            </div>
                        ))}
                    </div>
                ))}
                
                {/* Glossary */}
                {data.glossary && Object.keys(data.glossary).length > 0 && (
                    <div className="space-y-3">
                        <h2 className="text-xl font-semibold text-primary">Glossary</h2>
                        <div className="grid gap-2">
                            {Object.entries(data.glossary).map(([term, def], idx) => (
                                <div key={idx} className="p-3 rounded-lg bg-muted/50">
                                    <span className="font-medium">{term}:</span>{' '}
                                    <span className="text-muted-foreground">{def}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Quick Tips */}
                {data.quick_tips?.length > 0 && (
                    <div className="space-y-3">
                        <h2 className="text-xl font-semibold text-primary">Quick Tips</h2>
                        <ul className="space-y-2">
                            {data.quick_tips.map((tip, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                    <Zap className="h-4 w-4 text-yellow-500 mt-1 flex-shrink-0" />
                                    <span>{tip}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {/* FAQs */}
                {data.faqs?.length > 0 && (
                    <div className="space-y-3">
                        <h2 className="text-xl font-semibold text-primary">Frequently Asked Questions</h2>
                        <div className="space-y-4">
                            {data.faqs.map((faq, idx) => (
                                <div key={idx} className="p-4 rounded-lg bg-muted/30 border">
                                    <p className="font-medium mb-2">{faq.question}</p>
                                    <p className="text-muted-foreground">{faq.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="reference-docs-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <FileText className="h-8 w-8 text-primary" />
                            Reference Documents
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Generate or upload reference materials for your training content
                        </p>
                    </div>
                    <Button 
                        onClick={() => setShowCreateDialog(true)}
                        className="gap-2"
                        data-testid="create-doc-btn"
                    >
                        <Plus className="h-4 w-4" />
                        Add Document
                    </Button>
                </div>

                {/* Documents Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : documents.length === 0 ? (
                    <Card className="text-center py-12">
                        <CardContent>
                            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <h3 className="text-lg font-medium mb-2">No documents yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Generate AI reference docs or upload your own materials
                            </p>
                            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                                <Plus className="h-4 w-4" />
                                Add Your First Document
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {documents.map((doc) => {
                            const docType = DOC_TYPES.find(t => t.value === doc.content_type);
                            const Icon = docType?.icon || FileText;
                            
                            return (
                                <Card key={doc.id} className="group hover:shadow-lg transition-shadow">
                                    <CardHeader className="pb-3">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <CardTitle className="text-base truncate">
                                                    {doc.title}
                                                </CardTitle>
                                                <CardDescription className="flex items-center gap-2 mt-1">
                                                    <Clock className="h-3 w-3" />
                                                    {new Date(doc.created_at).toLocaleDateString()}
                                                </CardDescription>
                                            </div>
                                            <Badge 
                                                variant={doc.status === 'completed' ? 'default' : 
                                                        doc.status === 'processing' ? 'secondary' : 'destructive'}
                                                className="text-xs"
                                            >
                                                {doc.status === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                                                {doc.ai_generated ? 'AI' : 'Uploaded'}
                                            </Badge>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {doc.content_type && (
                                            <p className="text-xs text-muted-foreground mb-3 capitalize">
                                                {doc.content_type.replace('_', ' ')}
                                            </p>
                                        )}
                                        
                                        {doc.status === 'completed' && (
                                            <div className="flex gap-2">
                                                <Button 
                                                    size="sm" 
                                                    variant="outline"
                                                    onClick={() => viewDocument(doc)}
                                                    className="flex-1 gap-1"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                    View
                                                </Button>
                                                {!doc.ai_generated && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => downloadDocument(doc)}
                                                        className="flex-1 gap-1"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                        Download
                                                    </Button>
                                                )}
                                                <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    onClick={() => deleteDocument(doc.id)}
                                                    className="text-destructive hover:text-destructive"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Create Dialog */}
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>Add Reference Document</DialogTitle>
                        </DialogHeader>
                        
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="generate" className="gap-2">
                                    <Sparkles className="h-4 w-4" />
                                    AI Generate
                                </TabsTrigger>
                                <TabsTrigger value="upload" className="gap-2">
                                    <Upload className="h-4 w-4" />
                                    Upload
                                </TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="generate" className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>Source</Label>
                                    <Select 
                                        value={generateForm.sourceType}
                                        onValueChange={(v) => setGenerateForm(prev => ({ ...prev, sourceType: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="module">From Module</SelectItem>
                                            <SelectItem value="topic">From Topic</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {generateForm.sourceType === 'module' && (
                                    <div className="space-y-2">
                                        <Label>Select Module</Label>
                                        <Select 
                                            value={generateForm.module_id}
                                            onValueChange={(v) => setGenerateForm(prev => ({ ...prev, module_id: v }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Choose..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {modules.map(m => (
                                                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {generateForm.sourceType === 'topic' && (
                                    <div className="space-y-2">
                                        <Label>Select Topic</Label>
                                        <Select 
                                            value={generateForm.topic_id}
                                            onValueChange={(v) => setGenerateForm(prev => ({ ...prev, topic_id: v }))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Choose..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {topics.map(t => (
                                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label>Document Type</Label>
                                    <Select 
                                        value={generateForm.content_type}
                                        onValueChange={(v) => setGenerateForm(prev => ({ ...prev, content_type: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DOC_TYPES.map(t => (
                                                <SelectItem key={t.value} value={t.value}>
                                                    <span className="flex items-center gap-2">
                                                        <t.icon className="h-4 w-4" />
                                                        {t.label}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Title (Optional)</Label>
                                    <Input 
                                        value={generateForm.title}
                                        onChange={(e) => setGenerateForm(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Auto-generated if empty..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Custom Instructions (Optional)</Label>
                                    <Textarea 
                                        value={generateForm.custom_instructions}
                                        onChange={(e) => setGenerateForm(prev => ({ ...prev, custom_instructions: e.target.value }))}
                                        placeholder="Any specific focus areas or requirements..."
                                        rows={2}
                                    />
                                </div>

                                <Button 
                                    onClick={handleGenerate}
                                    disabled={generating}
                                    className="w-full"
                                >
                                    {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                                    Generate Document
                                </Button>
                            </TabsContent>
                            
                            <TabsContent value="upload" className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>File</Label>
                                    <Input 
                                        type="file"
                                        accept=".pdf,.docx,.doc,.txt,.md"
                                        onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files?.[0] }))}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Supported: PDF, DOCX, DOC, TXT, MD (max 10MB)
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label>Title (Optional)</Label>
                                    <Input 
                                        value={uploadForm.title}
                                        onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Uses filename if empty..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Description (Optional)</Label>
                                    <Textarea 
                                        value={uploadForm.description}
                                        onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Brief description..."
                                        rows={2}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Link to Module (Optional)</Label>
                                    <Select 
                                        value={uploadForm.module_id}
                                        onValueChange={(v) => setUploadForm(prev => ({ ...prev, module_id: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select module..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="">None</SelectItem>
                                            {modules.map(m => (
                                                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button 
                                    onClick={handleUpload}
                                    disabled={uploading || !uploadForm.file}
                                    className="w-full"
                                >
                                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                                    Upload Document
                                </Button>
                            </TabsContent>
                        </Tabs>
                    </DialogContent>
                </Dialog>

                {/* Document Viewer Dialog */}
                <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
                    <DialogContent className="max-w-4xl max-h-[90vh]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                {currentDoc?.title || 'Document'}
                            </DialogTitle>
                        </DialogHeader>
                        
                        <ScrollArea className="h-[70vh] pr-4">
                            {docContent && docContent.ai_generated ? (
                                renderDocContent(docContent)
                            ) : (
                                <div className="text-center py-12">
                                    <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                                    <p className="text-muted-foreground mb-4">
                                        This is an uploaded document. Download to view full content.
                                    </p>
                                    {currentDoc && (
                                        <Button onClick={() => downloadDocument(currentDoc)}>
                                            <Download className="h-4 w-4 mr-2" />
                                            Download File
                                        </Button>
                                    )}
                                </div>
                            )}
                        </ScrollArea>
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default ReferenceDocs;
